import { createTheme } from "@material-ui/core/styles";
import {
  primaryColor,
  infoColor,
  successColor,
  dangerColor,
  warningColor,
  roseColor,
  whiteColor,
  blackColor,
  grayColor,
  hexToRgb,
  defaultFont,
  boxShadow,
  primaryBoxShadow,
  infoBoxShadow,
  successBoxShadow,
  warningBoxShadow,
  dangerBoxShadow,
  roseBoxShadow,
  card,
  cardHeader,
  cardActions,
  title,
  cardTitle,
  cardSubtitle,
  cardLink,
} from "./jss/material-dashboard-pro-react.js";

const theme = createTheme({
  palette: {
    primary: {
      main: primaryColor[0],
      light: primaryColor[1],
      dark: primaryColor[4],
      contrastText: whiteColor,
    },
    secondary: {
      main: grayColor[2],
      light: grayColor[1],
      dark: grayColor[6],
      contrastText: whiteColor,
    },
    info: {
      main: infoColor[0],
      light: infoColor[1],
      dark: infoColor[4],
      contrastText: whiteColor,
    },
    success: {
      main: successColor[0],
      light: successColor[1],
      dark: successColor[4],
      contrastText: whiteColor,
    },
    warning: {
      main: warningColor[0],
      light: warningColor[1],
      dark: warningColor[4],
      contrastText: whiteColor,
    },
    danger: {
      main: dangerColor[0],
      light: dangerColor[1],
      dark: dangerColor[4],
      contrastText: whiteColor,
    },
    rose: {
      main: roseColor[0],
      light: roseColor[1],
      dark: roseColor[4],
      contrastText: whiteColor,
    },
    gray: grayColor,
    common: {
      black: blackColor,
      white: whiteColor,
    },
    text: {
      primary: "rgba(" + hexToRgb(blackColor) + ", 0.87)",
      secondary: grayColor[1],
      disabled: grayColor[3],
    },
    background: {
      paper: whiteColor,
      default: grayColor[12],
    },
    action: {
      active: "rgba(" + hexToRgb(blackColor) + ", 0.54)",
      hover: "rgba(" + hexToRgb(blackColor) + ", 0.04)",
      selected: "rgba(" + hexToRgb(blackColor) + ", 0.08)",
      disabled: "rgba(" + hexToRgb(blackColor) + ", 0.26)",
      disabledBackground: "rgba(" + hexToRgb(blackColor) + ", 0.12)",
    },
  },
  typography: {
    fontFamily: defaultFont.fontFamily,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: { ...title, fontSize: "2.5rem" },
    h2: { ...title, fontSize: "2rem" },
    h3: { ...title, fontSize: "1.75rem" },
    h4: { ...title, fontSize: "1.5rem" },
    h5: { ...title, fontSize: "1.25rem" },
    h6: { ...title, fontSize: "1rem" },
    subtitle1: { ...cardSubtitle },
    body1: { ...defaultFont },
    body2: { ...defaultFont, fontSize: "0.875rem" },
    button: {
      ...defaultFont,
      fontWeight: 500,
      textTransform: "uppercase",
    },
    caption: { ...defaultFont, fontSize: "0.75rem" },
    overline: {
      ...defaultFont,
      fontSize: "0.625rem",
      textTransform: "uppercase",
    },
  },
  overrides: {
    MuiCard: {
      root: {
        ...card,
        marginBottom: "21px",
        marginTop: "21px",
      },
    },
    MuiCardHeader: {
      root: {
        ...cardHeader,
        "&$primary": {
          background:
            "linear-gradient(60deg," +
            primaryColor[1] +
            "," +
            primaryColor[4] +
            ")",
          ...primaryBoxShadow,
        },
        "&$info": {
          background:
            "linear-gradient(60deg," + infoColor[1] + "," + infoColor[4] + ")",
          ...infoBoxShadow,
        },
        "&$success": {
          background:
            "linear-gradient(60deg," +
            successColor[1] +
            "," +
            successColor[4] +
            ")",
          ...successBoxShadow,
        },
        "&$warning": {
          background:
            "linear-gradient(60deg," +
            warningColor[1] +
            "," +
            warningColor[4] +
            ")",
          ...warningBoxShadow,
        },
        "&$danger": {
          background:
            "linear-gradient(60deg," +
            dangerColor[1] +
            "," +
            dangerColor[4] +
            ")",
          ...dangerBoxShadow,
        },
        "&$rose": {
          background:
            "linear-gradient(60deg," + roseColor[1] + "," + roseColor[4] + ")",
          ...roseBoxShadow,
        },
      },
      title: {
        ...cardTitle,
      },
      subheader: {
        ...cardSubtitle,
      },
    },
    MuiCardActions: {
      root: {
        ...cardActions,
      },
    },
    MuiCardContent: {
      root: {
        padding: "15px 20px",
        position: "relative",
      },
    },
    MuiLink: {
      root: {
        ...cardLink,
      },
    },
    MuiPaper: {
      elevation1: {
        boxShadow: "0 2px 5px 0 rgba(" + hexToRgb(primaryColor[0]) + ", 0.15)",
      },
      elevation2: {
        boxShadow: "0 4px 10px 0 rgba(" + hexToRgb(primaryColor[0]) + ", 0.15)",
      },
      elevation3: {
        boxShadow: "0 6px 15px 0 rgba(" + hexToRgb(primaryColor[0]) + ", 0.15)",
      },
    },
  },
  shape: {
    borderRadius: 6,
  },
  spacing: 8,
  shadows: [
    "none",
    "0 1px 4px 0 rgba(" + hexToRgb(blackColor) + ", 0.14)",
    boxShadow.boxShadow,
    primaryBoxShadow.boxShadow,
    infoBoxShadow.boxShadow,
    successBoxShadow.boxShadow,
    warningBoxShadow.boxShadow,
    dangerBoxShadow.boxShadow,
    roseBoxShadow.boxShadow,
    "0 8px 16px 0 rgba(" + hexToRgb(blackColor) + ", 0.14)", // elevation 9
    "0 10px 18px 0 rgba(" + hexToRgb(blackColor) + ", 0.16)", // elevation 10
    "0 12px 20px 0 rgba(" + hexToRgb(blackColor) + ", 0.18)", // elevation 11
    "0 14px 22px 0 rgba(" + hexToRgb(blackColor) + ", 0.20)", // elevation 12
    "0 16px 24px 0 rgba(" + hexToRgb(blackColor) + ", 0.22)", // elevation 13
    "0 18px 26px 0 rgba(" + hexToRgb(blackColor) + ", 0.24)", // elevation 14
    "0 20px 28px 0 rgba(" + hexToRgb(blackColor) + ", 0.26)", // elevation 15
    "0 22px 30px 0 rgba(" + hexToRgb(blackColor) + ", 0.28)", // elevation 16
    "0 24px 32px 0 rgba(" + hexToRgb(blackColor) + ", 0.30)", // elevation 17
    "0 26px 34px 0 rgba(" + hexToRgb(blackColor) + ", 0.32)", // elevation 18
    "0 28px 36px 0 rgba(" + hexToRgb(blackColor) + ", 0.34)", // elevation 19
    "0 30px 38px 0 rgba(" + hexToRgb(blackColor) + ", 0.36)", // elevation 20
    "0 32px 40px 0 rgba(" + hexToRgb(blackColor) + ", 0.38)", // elevation 21
    "0 34px 42px 0 rgba(" + hexToRgb(blackColor) + ", 0.40)", // elevation 22
    "0 36px 44px 0 rgba(" + hexToRgb(blackColor) + ", 0.42)", // elevation 23
    "0 38px 46px 0 rgba(" + hexToRgb(blackColor) + ", 0.44)", // elevation 24
  ],
});

export default theme;
